import React, { useEffect, useState, useMemo } from 'react';
import api from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';
import { isAdminLikeRole } from '../../../lib/roles';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { Banknote, Download, FileCheck, Search, AlertCircle, Loader2 } from 'lucide-react';
import { formatCurrency, formatDate } from '../../../lib/utils';

const fmtRM = (v) => `RM ${Number(v || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function DirectBankPayouts() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ count_pending: 0, count_paid: 0, total_amount_pending: 0 });
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Mark-paid dialog
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [markPaidClaim, setMarkPaidClaim] = useState(null);
  const [markPaidForm, setMarkPaidForm] = useState({ transaction_ref: '', transaction_date: '', notes: '' });
  const [evidenceFiles, setEvidenceFiles] = useState([]); // {file, base64, name}
  const [submitting, setSubmitting] = useState(false);

  const isAuthorised = !!user && (isAdminLikeRole(user.role) || user.role === 'account');

  useEffect(() => {
    if (!isAuthorised) return;
    let cancelled = false;
    setLoading(true);
    api.get('/commission-claims/payouts/direct-bank-queue', { params: { status: statusFilter } })
      .then(res => {
        if (cancelled) return;
        setItems(res.data?.items || []);
        setSummary(res.data?.summary || {});
      })
      .catch(err => {
        if (cancelled) return;
        toast.error(err.response?.data?.detail || 'Failed to load direct-bank queue');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [statusFilter, isAuthorised]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const t = search.trim().toLowerCase();
    return items.filter(c =>
      (c.claim_ref || '').toLowerCase().includes(t)
      || (c.direct_bank_payment?.agent_name || '').toLowerCase().includes(t)
      || (c.direct_bank_payment?.agent_code || '').toLowerCase().includes(t)
      || (c.direct_bank_payment?.bank_snapshot?.account_number || '').toLowerCase().includes(t)
      || (c.project_name || '').toLowerCase().includes(t)
    );
  }, [items, search]);

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const res = await api.get('/commission-claims/payouts/direct-bank-queue/export.csv', {
        params: { status: statusFilter },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `direct_bank_payouts_${statusFilter}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${filtered.length} rows`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const openMarkPaid = (claim) => {
    setMarkPaidClaim(claim);
    setMarkPaidForm({
      transaction_ref: '',
      transaction_date: new Date().toISOString().slice(0, 10),
      notes: '',
    });
    setEvidenceFiles([]);
    setMarkPaidOpen(true);
  };

  const handleEvidenceChange = (e) => {
    const files = Array.from(e.target.files || []);
    Promise.all(files.map(f => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve({ file: f, name: f.name, base64: reader.result, content_type: f.type });
      reader.readAsDataURL(f);
    }))).then(setEvidenceFiles);
  };

  const submitMarkPaid = async () => {
    if (!markPaidForm.transaction_ref.trim()) {
      toast.error('Transaction reference is required');
      return;
    }
    setSubmitting(true);
    try {
      // Upload each evidence file first to get URLs
      const uploaded = [];
      for (const ev of evidenceFiles) {
        try {
          const fd = new FormData();
          fd.append('file', ev.file);
          fd.append('document_type', 'direct_bank_evidence');
          const up = await api.post(
            `/commission-claims/${markPaidClaim.id}/upload-document`,
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' } },
          );
          uploaded.push({
            filename: ev.name,
            url: up.data?.url || up.data?.filename ? `/uploads/${up.data.filename}` : ev.base64,
            content_type: ev.content_type,
          });
        } catch (uerr) {
          // Fallback: store base64 inline if upload endpoint is unavailable
          uploaded.push({ filename: ev.name, url: ev.base64, content_type: ev.content_type });
        }
      }

      const payload = {
        transaction_ref: markPaidForm.transaction_ref.trim(),
        transaction_date: markPaidForm.transaction_date || null,
        notes: markPaidForm.notes || '',
        evidence_files: uploaded,
      };
      await api.post(`/commission-claims/${markPaidClaim.id}/direct-bank/mark-paid`, payload);
      toast.success(`Claim ${markPaidClaim.claim_ref} marked as paid`);
      setMarkPaidOpen(false);
      // Refresh
      setItems(prev => prev.filter(c => c.id !== markPaidClaim.id));
      setSummary(prev => ({
        ...prev,
        count_pending: Math.max(0, (prev.count_pending || 1) - 1),
        count_paid: (prev.count_paid || 0) + 1,
        total_amount_pending: Math.max(0, (prev.total_amount_pending || 0) - (markPaidClaim.direct_bank_payment?.amount || 0)),
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAuthorised) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Card><CardContent className="p-8 text-center text-gray-600">Only Admin or Account roles can view the direct-bank payouts queue.</CardContent></Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="direct-bank-payouts-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Banknote className="w-6 h-6 text-emerald-600" /> Direct Bank Payouts
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Approved fast-comm claims awaiting manual bank transfer to the agent. Process payments in your banking portal,
            then come back and click <span className="font-semibold">Mark Paid</span> with the transaction reference.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleExportCSV}
          disabled={exporting || filtered.length === 0}
          data-testid="export-csv-btn"
        >
          {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
          Export CSV ({filtered.length})
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="p-4">
          <p className="text-xs text-gray-500 uppercase">Pending</p>
          <p className="text-2xl font-bold text-amber-600 mt-1" data-testid="summary-count-pending">{summary.count_pending || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-gray-500 uppercase">Total Pending Amount</p>
          <p className="text-2xl font-bold text-emerald-700 mt-1" data-testid="summary-total-amount">{fmtRM(summary.total_amount_pending)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-gray-500 uppercase">Paid (this view)</p>
          <p className="text-2xl font-bold text-gray-700 mt-1">{summary.count_paid || 0}</p>
        </CardContent></Card>
      </div>

      {/* Filter tabs + search */}
      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList>
            <TabsTrigger value="pending" data-testid="filter-pending">Pending</TabsTrigger>
            <TabsTrigger value="paid" data-testid="filter-paid">Paid</TabsTrigger>
            <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by ref / agent / project / account..."
            className="pl-9"
            data-testid="payout-search"
          />
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <AlertCircle className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="font-medium">No {statusFilter === 'all' ? '' : statusFilter} direct-bank payouts</p>
              <p className="text-xs mt-1">Approved claims with payout-method "Direct Bank" will appear here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payouts-table">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-3 text-left">Claim</th>
                    <th className="px-4 py-3 text-left">Agent</th>
                    <th className="px-4 py-3 text-left">Bank</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-4 py-3 text-left">Project / Unit</th>
                    <th className="px-4 py-3 text-left">Approved</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(claim => {
                    const dbp = claim.direct_bank_payment || {};
                    const bank = dbp.bank_snapshot || {};
                    const isPaid = dbp.status === 'paid';
                    return (
                      <tr key={claim.id} className="border-t hover:bg-gray-50" data-testid={`payout-row-${claim.id}`}>
                        <td className="px-4 py-3">
                          <p className="font-mono font-semibold text-blue-700">{claim.claim_ref}</p>
                          <p className="text-xs text-gray-500">{claim.fast_comm}% Fast Comm</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-800">{dbp.agent_name || '—'}</p>
                          {dbp.agent_code && <p className="text-xs font-mono text-indigo-600">{dbp.agent_code}</p>}
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-700">{bank.bank_name || <span className="text-red-500 text-xs italic">No bank on file</span>}</p>
                          <p className="text-xs font-mono text-gray-500">{bank.account_number || '—'}</p>
                          <p className="text-[11px] text-gray-400 truncate max-w-[180px]">{bank.account_holder}</p>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <p className="font-mono font-bold text-emerald-700">{fmtRM(dbp.amount)}</p>
                          {(dbp.fee_total || 0) > 0 && (
                            <p className="text-[11px] text-gray-400">fee {fmtRM(dbp.fee_total)}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-gray-700">{claim.project_name || '—'}</p>
                          <p className="text-xs text-gray-500">{claim.unit_number || ''}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {claim.admin_approved_at || claim.approved_at ? formatDate(claim.admin_approved_at || claim.approved_at) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          {isPaid ? (
                            <Badge variant="success" className="bg-emerald-100 text-emerald-700">Paid</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">Pending</Badge>
                          )}
                          {isPaid && dbp.transaction_ref && (
                            <p className="text-[11px] font-mono text-gray-500 mt-1">ref: {dbp.transaction_ref}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {!isPaid && (
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => openMarkPaid(claim)}
                              disabled={!bank.account_number}
                              data-testid={`mark-paid-btn-${claim.id}`}
                            >
                              <FileCheck className="w-4 h-4 mr-1" /> Mark Paid
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mark-paid dialog */}
      <Dialog open={markPaidOpen} onOpenChange={(o) => !submitting && setMarkPaidOpen(o)}>
        <DialogContent className="max-w-lg" data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle>Mark Direct Bank Payment as Paid</DialogTitle>
            <DialogDescription>
              Confirms that the agent has been paid via bank transfer. The agent will receive an in-app notification.
            </DialogDescription>
          </DialogHeader>
          {markPaidClaim && (
            <div className="space-y-4">
              <div className="bg-gray-50 border rounded p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-gray-500">Claim:</span> <span className="font-mono font-semibold">{markPaidClaim.claim_ref}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Agent:</span> <span>{markPaidClaim.direct_bank_payment?.agent_name}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Bank:</span> <span>{markPaidClaim.direct_bank_payment?.bank_snapshot?.bank_name} {markPaidClaim.direct_bank_payment?.bank_snapshot?.account_number}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Amount:</span> <span className="font-bold text-emerald-700">{fmtRM(markPaidClaim.direct_bank_payment?.amount)}</span></div>
              </div>
              <div>
                <Label>Transaction Reference *</Label>
                <Input
                  value={markPaidForm.transaction_ref}
                  onChange={(e) => setMarkPaidForm(prev => ({ ...prev, transaction_ref: e.target.value }))}
                  placeholder="e.g., TXN-987654321"
                  data-testid="txn-ref-input"
                />
              </div>
              <div>
                <Label>Transaction Date</Label>
                <Input
                  type="date"
                  value={markPaidForm.transaction_date}
                  onChange={(e) => setMarkPaidForm(prev => ({ ...prev, transaction_date: e.target.value }))}
                  data-testid="txn-date-input"
                />
              </div>
              <div>
                <Label>Bank Slip / Evidence (optional, multiple allowed)</Label>
                <Input
                  type="file"
                  multiple
                  accept="image/*,application/pdf"
                  onChange={handleEvidenceChange}
                  data-testid="evidence-input"
                />
                {evidenceFiles.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">{evidenceFiles.length} file(s) ready: {evidenceFiles.map(e => e.name).join(', ')}</p>
                )}
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={markPaidForm.notes}
                  onChange={(e) => setMarkPaidForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any context — banking portal used, batch ref, etc."
                  rows={2}
                  data-testid="notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkPaidOpen(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={submitMarkPaid} disabled={submitting || !markPaidForm.transaction_ref.trim()} data-testid="confirm-mark-paid">
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileCheck className="w-4 h-4 mr-1" />}
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
